import {
    PlusIcon,
    SyncIcon,
    TrashIcon
} from "@primer/octicons-react";
import { Box } from '@primer/react';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Transition } from 'semantic-ui-react';
import { AnimationConfirm } from "../../../components/confirm";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import Button from "../../../components/ui/button";
import Text from "../../../components/ui/text";
import { PageBaseStatus } from "../../../models/pageBaseStatus";
import { sf_hinhthucmiengiam_group } from "../../../models/response/hinh-thuc-mien-giam/sf_hinhthucmiengiam_group";
import { closeHinhThucMienGiamGroupDetailModal, deleteHinhThucMienGiamGroupStart, loadHinhThucMienGiamGroupStart, saveHinhThucMienGiamGroupStart, showHinhThucMienGiamGroupDeleteConfirm, showHinhThucMienGiamGroupDetailModal } from "../../../state/actions/hinhThucMienGiamGroupActions";
import { RootState } from "../../../state/reducers";
import NhomMienGiamEditModal from "./NhomMienGiamEditModal";

const HinhThucMienGiamGroupPage = () => {
    const [danhSachNhomMienGiamSelectedIds, setDanhSachNhomMienGiamSelectedIds] = useState<number[]>([]);
    const { dm_coso_id } = useSelector((state: RootState) => state.common)
    const dispatch = useDispatch();
    const state = useSelector((state: RootState) => state.hinhThucMienGiamGroup)
    const {
        status,
        sf_hinhthucmiengiam_groups,
        sf_hinhthucmiengiam_group_editing,
        is_show_detail_modal,
        is_show_delete_confirm
    } = state;

    const dataSource = useMemo(() => {
        return sf_hinhthucmiengiam_groups.filter(x => x.dm_coso_id === dm_coso_id)
    }, [sf_hinhthucmiengiam_groups, dm_coso_id])

    useEffect(() => {
        dispatch(loadHinhThucMienGiamGroupStart())
    }, [])

    const handleRefresh = useCallback(() => {
        dispatch(loadHinhThucMienGiamGroupStart());
        setDanhSachNhomMienGiamSelectedIds([])
    }, [])

    const handleCloseDeleteConfirm = useCallback(() => {
        dispatch(showHinhThucMienGiamGroupDeleteConfirm(false))
    }, [])

    const handleShowDeleteConfirm = useCallback(() => {
        dispatch(showHinhThucMienGiamGroupDeleteConfirm(true))
    }, [])

    const handleBtnInsertClick = useCallback(() => {
        dispatch(showHinhThucMienGiamGroupDetailModal());
    }, []);

    const handleBtnEditClicked = (data: sf_hinhthucmiengiam_group) => {
        dispatch(showHinhThucMienGiamGroupDetailModal(data));
    }

    const handleConfirmDelete = useCallback(() => {
        dispatch(deleteHinhThucMienGiamGroupStart(danhSachNhomMienGiamSelectedIds))
        setDanhSachNhomMienGiamSelectedIds([])
    }, [danhSachNhomMienGiamSelectedIds]);

    const handleCloseDetailModal = useCallback(() => {
        dispatch(closeHinhThucMienGiamGroupDetailModal())
    }, [])

    const handleSubmitDetailModal = useCallback((data: sf_hinhthucmiengiam_group) => {
        dispatch(saveHinhThucMienGiamGroupStart(data))
        dispatch(closeHinhThucMienGiamGroupDetailModal())
    }, [])

    const columns = useMemo<IDataGridColumn[]>(() => {
        return [
            {
                caption: "Sửa",
                id: "edit",
                width: 50,
                align: "center",
                cellRender: (data: any) => (
                    <a id={`btnEdit${data.id}`} style={{ cursor: "pointer", color: "#3c22ff" }}>
                        <i onClick={() => handleBtnEditClicked(data)} className="cmd-edit-icon fas fa-edit"></i>
                    </a>
                ),
            },
            {
                caption: "Áp dụng",
                id: "is_active",
                dataField: "is_active",
                width: 100,
                align: "center",
                cellRender: (data: any) => (
                    <input
                        type="checkbox"
                        checked={data.is_active === true}
                        readOnly
                        style={{ cursor: "default" }}
                    />
                ),
            },
            {
                caption: "Mã nhóm miễn giảm",
                id: "ma_nhom_mien_giam",
                dataField: "ma_nhom_mien_giam",
                width: 200,
            },
            {
                caption: "Tên nhóm miễn giảm",
                id: "ten_nhom_mien_giam",
                dataField: "ten_nhom_mien_giam",
                width: 300,
            },
            {
                caption: "Thứ tự",
                id: "thu_tu",
                dataField: "thu_tu",
                width: 100,
                align: "center",
            },
        ];
    }, [handleBtnEditClicked]);

    return (
        <React.Fragment>
            <Box sx={{ p: 2, pt: 0 }}>
                <Transition animation="scale"
                    visible={true}
                    duration={100}
                    transitionOnMount={true}>
                    <Box>
                        <Box sx={{ m: 1 }}>
                            <DataGrid
                                dataSource={dataSource}
                                columns={columns}
                                keyExpr="id"
                                height={window.innerHeight - 200}
                                isLoading={status === PageBaseStatus.is_loading}
                                title={
                                    <Text text="Danh sách nhóm miễn giảm" sx={{ fontSize: 16, fontWeight: 600 }} />
                                }
                                searchEnable={true}
                                selection={{
                                    mode: "multiple",
                                    selectedRowKeys: danhSachNhomMienGiamSelectedIds,
                                    onSelectionChanged: (keys) => {
                                        setDanhSachNhomMienGiamSelectedIds(keys)
                                    },
                                }}
                                actionComponent={
                                    <Box sx={{
                                        display: "flex"
                                    }}>
                                        <Button sx={{ ml: 2 }} variant="default" size="small" leadingVisual={SyncIcon} onClick={handleRefresh}>Làm mới</Button>
                                        <Button sx={{ ml: 2 }} variant="primary" size="small" leadingVisual={PlusIcon} onClick={handleBtnInsertClick}>Thêm mới</Button>
                                        {danhSachNhomMienGiamSelectedIds && danhSachNhomMienGiamSelectedIds.length > 0 &&
                                            <Button sx={{ ml: 2 }} leadingVisual={TrashIcon} variant="danger" size="small" onClick={handleShowDeleteConfirm}>Xóa {danhSachNhomMienGiamSelectedIds.length} nhóm miễn giảm</Button>
                                        }
                                    </Box>
                                }
                                paging={{
                                    enable: true,
                                    pageSize: 50,
                                    pageSizeItems: [50, 100, 200, 500]
                                }}
                                exportConfig={{
                                    enable: true,
                                }}
                                filterRow={{
                                    enable: true
                                }}
                            />
                            {is_show_detail_modal && <NhomMienGiamEditModal
                                title={sf_hinhthucmiengiam_group_editing ? `Cập nhật` : "Thêm mới"}
                                sf_hinhthucmiengiam_group={sf_hinhthucmiengiam_group_editing}
                                onClose={handleCloseDetailModal}
                                onSubmit={handleSubmitDetailModal}
                                is_saving={status === PageBaseStatus.is_saving}
                                animationOf={sf_hinhthucmiengiam_group_editing ? `#btnEdit${sf_hinhthucmiengiam_group_editing.id}` : "#btnInsert"}
                                sf_hinhthucmiengiam_groups={dataSource}
                            />
                            }
                        </Box>
                    </Box>
                </Transition>
                {is_show_delete_confirm &&
                    <AnimationConfirm
                        onClose={handleCloseDeleteConfirm}
                        onConfirmed={handleConfirmDelete}
                        text={`Bạn có chắc chắn muốn xóa ${danhSachNhomMienGiamSelectedIds.length} dòng đã chọn ?`}
                        is_saving={status === PageBaseStatus.is_deleting}
                        text_close_button="Không xóa"
                        text_confirm_button="Xóa"
                        type='danger'
                        animationOf='#btnDelete'
                    />
                }
            </Box>
        </React.Fragment>
    );
}
export default HinhThucMienGiamGroupPage;
